import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { io } from "socket.io-client";
import {
  Monitor, SignOut, Copy, ArrowsClockwise, Broadcast, Plus, Trash,
  ArrowUpRight, ArrowDownLeft, DesktopTower, Circle, DeviceMobile, ShieldCheck,
  Check, PencilSimple, DownloadSimple,
} from "@phosphor-icons/react";
import api, { SOCKET_URL, SOCKET_PATH, getDeviceKey, detectDeviceName, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function copy(text, label) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(`${label} copied`),
    () => toast.error("Copy failed")
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function fmtDuration(sec) {
  if (!sec && sec !== 0) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [connectId, setConnectId] = useState("");
  const [pinInput, setPinInput] = useState("");
  const [devices, setDevices] = useState([]);
  const [connections, setConnections] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [thisDevice, setThisDevice] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [onlineIds, setOnlineIds] = useState(new Set());
  const [autoApprove, setAutoApprove] = useState(() => localStorage.getItem("nexus_auto_approve") !== "0");
  const [pairCode, setPairCode] = useState(null);
  const [pairBusy, setPairBusy] = useState(false);
  const [showAgentHelp, setShowAgentHelp] = useState(false);
  const socketRef = useRef(null);

  const loadAll = async () => {
    try {
      const [d, c] = await Promise.all([api.get("/devices"), api.get("/connections")]);
      setDevices(d.data);
      setConnections(c.data);
    } catch (e) {}
  };

  // Register THIS browser/PC as its own device (unique Session ID per machine)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.post("/devices/register-self", {
          browser_key: getDeviceKey(),
          name: detectDeviceName(),
        });
        setThisDevice(res.data);
      } catch (e) {}
      loadAll();
    })();
  }, []);

  // Live presence: subscribe to PCs starting/stopping their share
  useEffect(() => {
    const socket = io(SOCKET_URL, { path: SOCKET_PATH, transports: ["websocket", "polling"] });
    socketRef.current = socket;
    socket.on("connect", () => socket.emit("watch_presence"));
    socket.on("presence_snapshot", (d) => setOnlineIds(new Set(d.rooms || [])));
    socket.on("presence_update", (d) => {
      setOnlineIds((prev) => {
        const next = new Set(prev);
        if (d.online) next.add(d.room); else next.delete(d.room);
        return next;
      });
    });
    return () => { socket.disconnect(); };
  }, []);

  const regenPin = async () => {
    if (!thisDevice) return;
    try {
      const res = await api.post(`/devices/${thisDevice.device_id}/regenerate-pin`);
      setThisDevice({ ...thisDevice, pin: res.data.pin });
      toast.success("PIN regenerated");
    } catch (e) { toast.error("Failed to regenerate PIN"); }
  };

  const startHosting = () => {
    if (!thisDevice) { toast.error("Preparing this device..."); return; }
    navigate(`/session/${thisDevice.connect_id}?role=host&auto=${autoApprove ? "1" : "0"}`);
  };

  const toggleAutoApprove = () => {
    setAutoApprove((v) => {
      const next = !v;
      localStorage.setItem("nexus_auto_approve", next ? "1" : "0");
      return next;
    });
  };

  const connect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    try {
      const res = await api.post("/sessions/connect", {
        connect_id: connectId.replace(/\s/g, ""),
        pin: pinInput,
      });
      navigate(`/session/${res.data.room}?role=viewer&conn=${res.data.connection_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection failed");
    } finally {
      setConnecting(false);
    }
  };

  const connectToDevice = async (d) => {
    try {
      const res = await api.post("/sessions/connect-device", { device_id: d.device_id });
      navigate(`/session/${res.data.room}?role=viewer&conn=${res.data.connection_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection failed");
    }
  };

  const saveRename = async () => {
    if (!renameValue.trim() || !thisDevice) { setRenaming(false); return; }
    try {
      await api.patch(`/devices/${thisDevice.device_id}`, { name: renameValue.trim() });
      setThisDevice({ ...thisDevice, name: renameValue.trim() });
      setRenaming(false);
      loadAll();
      toast.success("Renamed");
    } catch (e) { toast.error("Rename failed"); }
  };

  const removeDevice = async (id) => {
    try { await api.delete(`/devices/${id}`); loadAll(); toast.success("Device removed"); }
    catch (e) { toast.error("Failed"); }
  };

  const generatePairCode = async () => {
    setPairBusy(true);
    try {
      const res = await api.post("/agent/pair/init");
      setPairCode(res.data.pairing_code);
      setShowAgentHelp(true);
    } catch (e) { toast.error("Could not generate setup code"); }
    finally { setPairBusy(false); }
  };

  const idDisplay = (thisDevice?.connect_id || "").replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3");
  const pin = thisDevice?.pin || "----";
  const otherDevices = devices.filter((d) => d.browser_key !== getDeviceKey() && d.device_id !== thisDevice?.device_id);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-16 border-r border-zinc-900 items-center py-6 gap-8">
        <Monitor size={26} weight="duotone" className="text-[#0055FF]" />
        <div className="flex-1" />
        <button onClick={logout} data-testid="logout-btn" className="text-zinc-500 hover:text-[#FF3B30] transition-colors" title="Logout">
          <SignOut size={22} />
        </button>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Topbar */}
        <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-zinc-900">
          <div>
            <h1 className="font-heading font-black text-2xl tracking-tighter">Console</h1>
            <p className="font-mono text-xs text-zinc-500">Signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <button
                onClick={() => navigate("/admin")}
                data-testid="admin-link-btn"
                className="hidden sm:flex items-center gap-2 border border-[#0055FF]/50 text-[#00E5FF] hover:bg-[#0055FF]/10 transition-colors px-4 py-2 font-mono text-[11px] uppercase tracking-widest"
              >
                <ShieldCheck size={16} /> Admin
              </button>
            )}
            {user?.picture
              ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-zinc-800" />
              : <div className="w-9 h-9 rounded-full bg-[#0055FF] flex items-center justify-center font-heading font-black text-sm">{(user?.name || "?")[0]}</div>}
            <button onClick={logout} className="md:hidden text-zinc-500" data-testid="logout-btn-mobile"><SignOut size={22} /></button>
          </div>
        </div>

        <div className="p-6 md:p-10 grid lg:grid-cols-2 gap-6 max-w-6xl">
          {/* Your ID + PIN */}
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="border border-zinc-800 bg-[#121214] p-8 relative overflow-hidden scanlines"
            data-testid="your-id-panel"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <DesktopTower size={18} className="text-[#00E5FF]" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">This PC</span>
              </div>
              {renaming ? (
                <div className="flex items-center gap-2">
                  <input
                    data-testid="rename-input"
                    autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveRename()}
                    className="bg-zinc-900 border border-zinc-800 px-2 py-1 text-sm text-white focus:border-[#0055FF] outline-none w-40"
                  />
                  <button onClick={saveRename} data-testid="rename-save-btn" className="text-[#34C759] hover:text-green-400"><Check size={18} /></button>
                </div>
              ) : (
                <button
                  onClick={() => { setRenameValue(thisDevice?.name || ""); setRenaming(true); }}
                  data-testid="rename-device-btn"
                  className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors font-mono text-xs"
                >
                  <span data-testid="this-device-name" className="truncate max-w-[160px]">{thisDevice?.name || "This device"}</span>
                  <PencilSimple size={14} />
                </button>
              )}
            </div>

            <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-600 mb-2">Session ID</p>
            <div className="flex items-center gap-3 mb-6">
              <span data-testid="your-connect-id" className="font-mono font-bold text-4xl md:text-5xl tracking-tight text-white">{idDisplay || "— — —"}</span>
              <button onClick={() => copy(thisDevice?.connect_id, "ID")} className="text-zinc-500 hover:text-white transition-colors" data-testid="copy-id-btn"><Copy size={22} /></button>
            </div>

            <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-600 mb-2">PIN</p>
            <div className="flex items-center gap-3">
              <span data-testid="your-pin" className="font-mono font-bold text-4xl md:text-5xl tracking-tight text-[#0055FF]">{pin}</span>
              <button onClick={() => copy(pin, "PIN")} className="text-zinc-500 hover:text-white transition-colors" data-testid="copy-pin-btn"><Copy size={20} /></button>
              <button onClick={regenPin} className="text-zinc-500 hover:text-[#00E5FF] transition-colors" title="Regenerate PIN" data-testid="regen-pin-btn"><ArrowsClockwise size={20} /></button>
            </div>

            <button
              onClick={toggleAutoApprove}
              data-testid="auto-approve-toggle"
              className="mt-8 w-full flex items-center justify-between border border-zinc-800 px-4 py-3 hover:border-zinc-600 transition-colors"
            >
              <span className="flex items-center gap-2">
                <ShieldCheck size={16} className={autoApprove ? "text-[#34C759]" : "text-zinc-500"} />
                <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-300">Auto-approve connections</span>
              </span>
              <span className={`relative w-10 h-5 transition-colors ${autoApprove ? "bg-[#34C759]" : "bg-zinc-700"}`} data-testid="auto-approve-state" data-on={autoApprove}>
                <span className={`absolute top-0.5 h-4 w-4 bg-white transition-all ${autoApprove ? "left-[22px]" : "left-0.5"}`} />
              </span>
            </button>

            <button
              onClick={startHosting}
              disabled={!thisDevice}
              data-testid="start-hosting-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white font-mono text-sm uppercase tracking-widest py-3.5 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Broadcast size={18} weight="fill" /> Share This Screen
            </button>
            <p className="mt-3 font-mono text-[11px] text-zinc-600 text-center">Others use this ID + PIN to control this PC</p>
          </motion.div>

          {/* Connect to a device */}
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.05 }}
            className="border border-zinc-800 bg-[#121214] p-8"
            data-testid="connect-panel"
          >
            <div className="flex items-center gap-2 mb-6">
              <ArrowUpRight size={18} className="text-[#00E5FF]" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Connect To A Remote Screen</span>
            </div>
            <form onSubmit={connect} className="space-y-4">
              <div>
                <label className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Remote ID</label>
                <input
                  data-testid="connect-id-input"
                  value={connectId} onChange={(e) => setConnectId(e.target.value)} required
                  placeholder="708 416 916"
                  className="w-full mt-1.5 bg-zinc-900 border border-zinc-800 px-4 py-3 text-white font-mono text-lg tracking-widest focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none transition-colors"
                />
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">PIN</label>
                <input
                  data-testid="connect-pin-input"
                  value={pinInput} onChange={(e) => setPinInput(e.target.value)} required
                  placeholder="4-digit"
                  className="w-full mt-1.5 bg-zinc-900 border border-zinc-800 px-4 py-3 text-white font-mono text-lg tracking-widest focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none transition-colors"
                />
              </div>
              <button
                type="submit" disabled={connecting}
                data-testid="connect-session-btn"
                className="w-full flex items-center justify-center gap-2 bg-white text-black font-mono text-sm uppercase tracking-widest py-3.5 hover:bg-zinc-200 transition-colors disabled:opacity-50"
              >
                {connecting ? "Connecting..." : <>Connect <ArrowUpRight size={18} weight="bold" /></>}
              </button>
            </form>
            <p className="mt-4 font-mono text-[11px] text-zinc-600 leading-relaxed">
              Tip: to control this PC from elsewhere, log in on that PC and open "This PC" — or share this ID + PIN with anyone.
            </p>
          </motion.div>

          {/* My Other PCs */}
          <div className="border border-zinc-800 bg-[#121214] p-8" data-testid="devices-panel">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <DeviceMobile size={18} className="text-[#00E5FF]" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">My Other PCs</span>
              </div>
              <span className="font-mono text-[11px] text-zinc-600">{otherDevices.length} device{otherDevices.length === 1 ? "" : "s"}</span>
            </div>

            <div className="space-y-2">
              {otherDevices.length === 0 && (
                <p className="text-zinc-600 font-mono text-sm leading-relaxed">
                  No other PCs yet. Log in to this account on your laptop, office, or home PC — each one registers here automatically and you can connect to it with one click.
                </p>
              )}
              {otherDevices.map((d) => {
                const isOnline = onlineIds.has(d.connect_id);
                return (
                <div key={d.device_id} className="flex items-center justify-between border border-zinc-800 px-4 py-3 hover:border-zinc-600 transition-colors" data-testid="device-row">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="relative flex items-center justify-center" data-testid="device-status-dot">
                      <Circle size={9} weight="fill" className={isOnline ? "text-[#34C759]" : "text-zinc-600"} />
                      {isOnline && <span className="absolute w-2.5 h-2.5 rounded-full bg-[#34C759] animate-ping opacity-75" />}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate flex items-center gap-2">
                        {d.name}
                        {d.type === "agent" && (
                          <span className="font-mono text-[9px] uppercase tracking-wider bg-[#00E5FF]/15 text-[#00E5FF] px-1.5 py-0.5" data-testid="unattended-badge">Unattended</span>
                        )}
                      </p>
                      <p className="font-mono text-[11px] text-zinc-500">
                        ID {(d.connect_id || "").replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")} · <span className={isOnline ? "text-[#34C759]" : "text-zinc-500"}>{isOnline ? "sharing now" : "offline"}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => connectToDevice(d)}
                      disabled={!isOnline}
                      title={isOnline ? "Connect" : "This PC must click 'Share This Screen' first"}
                      data-testid="device-connect-btn"
                      className={`flex items-center gap-1.5 transition-colors px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider ${isOnline ? "bg-[#0055FF] text-white hover:bg-blue-700" : "bg-zinc-800 text-zinc-600 cursor-not-allowed"}`}
                    >
                      <ArrowUpRight size={14} weight="bold" /> Connect
                    </button>
                    <button onClick={() => removeDevice(d.device_id)} className="text-zinc-600 hover:text-[#FF3B30] transition-colors" data-testid="delete-device-btn"><Trash size={16} /></button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Connection history */}
          <div className="border border-zinc-800 bg-[#121214] p-8" data-testid="history-panel">
            <div className="flex items-center gap-2 mb-6">
              <ArrowsClockwise size={18} className="text-[#00E5FF]" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Recent Connections</span>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {connections.length === 0 && <p className="text-zinc-600 font-mono text-sm">No connections yet.</p>}
              {connections.map((c) => (
                <div key={c.connection_id} className="flex items-center justify-between py-2.5 border-b border-zinc-900" data-testid="history-row">
                  <div className="flex items-center gap-3">
                    {c.direction === "outgoing"
                      ? <ArrowUpRight size={16} className="text-[#0055FF]" />
                      : <ArrowDownLeft size={16} className="text-[#00E5FF]" />}
                    <div>
                      <p className="text-sm text-white">{c.direction === "outgoing" ? `→ ${c.host_name}` : `← ${c.viewer_name}`}</p>
                      <p className="font-mono text-[11px] text-zinc-500">{fmtDate(c.started_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`font-mono text-[11px] uppercase ${c.status === "active" ? "text-[#34C759]" : "text-zinc-500"}`}>{c.status}</span>
                    <p className="font-mono text-[11px] text-zinc-600">{fmtDuration(c.duration)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Unattended Access (native agent) */}
          <div className="border border-zinc-800 bg-[#121214] p-8 lg:col-span-2" data-testid="unattended-panel">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-[#00E5FF]" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Unattended Access · Zero-click Agent</span>
              </div>
              <span className="font-mono text-[11px] text-zinc-600 hidden sm:block">Windows · macOS</span>
            </div>

            <p className="text-sm text-zinc-400 max-w-2xl leading-relaxed mb-6">
              Install the tiny background agent on a PC to reach it with <span className="text-white">zero clicks</span>, even after a reboot — no one needs to be at that computer to accept. Two ways: a <span className="text-white">one-click installer</span> (no Python needed — build it once via the included scripts/GitHub Actions), or run from source below. Generate a one-time setup code, then pair.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={generatePairCode}
                disabled={pairBusy}
                data-testid="generate-pair-btn"
                className="flex items-center gap-2 bg-[#0055FF] text-white font-mono text-xs uppercase tracking-widest px-5 py-3 hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <ShieldCheck size={16} /> {pairBusy ? "..." : "Generate Setup Code"}
              </button>
              <a
                href={`${API}/agent/download`}
                data-testid="download-agent-btn"
                className="flex items-center gap-2 border border-zinc-700 text-zinc-200 font-mono text-xs uppercase tracking-widest px-5 py-3 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors"
              >
                <DownloadSimple size={16} /> Download Agent
              </a>
            </div>

            {pairCode && (
              <div className="mt-6 border border-zinc-800 p-5" data-testid="pair-code-box">
                <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Your one-time setup code (valid 15 min)</p>
                <div className="flex items-center gap-3">
                  <span data-testid="pair-code" className="font-mono font-bold text-3xl tracking-widest text-[#00E5FF]">{pairCode}</span>
                  <button onClick={() => copy(pairCode, "Setup code")} className="text-zinc-500 hover:text-white transition-colors"><Copy size={18} /></button>
                </div>
              </div>
            )}

            {showAgentHelp && (
              <div className="mt-4 border border-zinc-800 p-5 space-y-3" data-testid="agent-help">
                <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Setup steps (on the PC you want to reach)</p>
                <ol className="text-sm text-zinc-400 space-y-2 list-decimal list-inside">
                  <li>Download & unzip the agent, then install deps: <code className="text-[#00E5FF]">pip install -r requirements.txt</code></li>
                  <li>Pair it (paste your code):</li>
                </ol>
                <div className="bg-zinc-900 border border-zinc-800 p-3 font-mono text-xs text-zinc-200 overflow-x-auto flex items-center justify-between gap-3">
                  <span data-testid="pair-command">python nexus_agent.py --server {process.env.REACT_APP_BACKEND_URL} --pair {pairCode || "YOUR_CODE"}</span>
                  <button onClick={() => copy(`python nexus_agent.py --server ${process.env.REACT_APP_BACKEND_URL} --pair ${pairCode || "YOUR_CODE"}`, "Command")} className="text-zinc-500 hover:text-white shrink-0"><Copy size={15} /></button>
                </div>
                <p className="font-mono text-[11px] text-zinc-600 leading-relaxed">
                  The PC then appears here with an <span className="text-[#00E5FF]">Unattended</span> badge. See README in the zip for auto-start-on-reboot (Task Scheduler / launchd) and building a standalone .exe/.app.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
